import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const features = [
  { icon: '🚗', title: 'Dedicated Fleet', desc: 'Access to a curated fleet of executive, SUV, and commercial vehicles tailored to your business needs.' },
  { icon: '📅', title: 'Monthly Billing', desc: 'Simplified monthly invoicing with net payment terms. No surprises, no hidden fees.' },
  { icon: '👔', title: 'Chauffeur Service', desc: 'Professional, vetted chauffeurs available for executive travel and airport transfers.' },
  { icon: '🔧', title: 'Maintenance Bundles', desc: 'Optional full-service maintenance packages included in your lease terms.' },
  { icon: '📊', title: 'Fleet Dashboard', desc: 'Real-time visibility into your fleet usage, driver assignments, and cost tracking.' },
  { icon: '🛡️', title: 'Insurance Options', desc: 'Comprehensive insurance packages available as part of your leasing agreement.' },
];

const tiers = [
  { name: 'Starter', vehicles: '1–3 vehicles', price: 'From KES 45,000', period: '/vehicle/month', color: '#60a5fa', features: ['Economy & Compact', 'Self-drive only', 'Monthly billing', 'Basic support'] },
  { name: 'Business', vehicles: '4–10 vehicles', price: 'From KES 65,000', period: '/vehicle/month', color: '#ffd700', featured: true, features: ['Sedan & SUV fleet', 'Chauffeur option', 'Priority support', 'Fleet dashboard', 'Maintenance bundle'] },
  { name: 'Enterprise', vehicles: '10+ vehicles', price: 'Custom pricing', period: 'negotiated', color: '#a78bfa', features: ['Full fleet customization', 'Dedicated account manager', 'Custom SLA terms', 'Insurance included', 'API integration'] },
];

const CorporateLeasingScreen = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {/* Nav */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm font-medium">Back to Home</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🏢</span>
            <span className="text-white font-black text-lg">Corporate Leasing</span>
          </div>
          <button onClick={() => navigate('/lease-request')} className="btn-primary text-sm py-2 px-5">
            Get a Quote
          </button>
        </div>
      </div>
      {/* Hero */}
      <section className="relative overflow-hidden py-24 px-4" style={{ background: 'linear-gradient(135deg, #0d1f3c 0%, #0a1628 60%, #0d1f3c 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,215,0,1) 1px, transparent 0)', backgroundSize: '48px 48px' }} />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10" style={{ background: 'radial-gradient(ellipse at 100% 50%, #ffd700 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6 border" style={{ background: 'rgba(255,215,0,0.08)', borderColor: 'rgba(255,215,0,0.2)' }}>
              <span className="w-2 h-2 rounded-full bg-[#ffd700] animate-pulse" />
              <span className="text-[#ffd700] text-sm font-semibold">B2B Fleet Solutions</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6 leading-tight">
              Fleet Solutions<br />
              <span className="text-[#ffd700]">Built for Business</span>
            </h1>
            <p className="text-white/55 text-xl leading-relaxed mb-10 max-w-2xl">
              Monthly vehicle leasing with flexible terms, chauffeur options, and maintenance bundles. Trusted by 50+ companies across East Africa.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => navigate('/lease-request')} className="btn-primary text-base px-8 py-4">
                Request a Quote <Icon name="ArrowRight" size={18} />
              </button>
              <button onClick={() => navigate('/corporate-dashboard')} className="btn-dark text-base px-8 py-4">
                Corporate Login
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Trust Indicators */}
      <section className="py-10 border-y border-white/8" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 text-center">
            {[
              { value: '50+', label: 'Corporate Clients' },
              { value: '200+', label: 'Vehicles Leased' },
              { value: '3 yrs', label: 'Average Contract' },
              { value: '99%', label: 'Renewal Rate' },
            ]?.map(stat => (
              <div key={stat?.label}>
                <div className="text-[#ffd700] font-black text-3xl">{stat?.value}</div>
                <div className="text-white/40 text-sm">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Features Bento */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ background: 'rgba(255,215,0,0.08)', borderColor: 'rgba(255,215,0,0.2)' }}>
              <span className="text-[#ffd700] text-sm font-semibold">Why OnTheGo Corporate</span>
            </div>
            <h2 className="text-4xl font-black text-white">Everything Your Business Needs</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features?.map((f, i) => (
              <div key={f?.title} className={`rounded-3xl p-7 border transition-all hover:scale-[1.02] ${i === 0 ? 'md:col-span-2' : ''}`} style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}>
                <div className="text-4xl mb-4">{f?.icon}</div>
                <h3 className="text-white font-bold text-lg mb-2">{f?.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{f?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-20 px-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-black text-white mb-3">How It Works</h2>
            <p className="text-white/40 text-lg">From request to road in 5 business days</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Submit Request', desc: 'Fill out your fleet requirements — vehicle types, quantity, duration, and budget.' },
              { step: '02', title: 'Receive Quote', desc: 'Our team builds a custom quote within 24 hours with pricing and terms.' },
              { step: '03', title: 'Sign Contract', desc: 'Review and sign the lease agreement digitally. Simple, fast, legally binding.' },
              { step: '04', title: 'Fleet Delivered', desc: 'Vehicles delivered to your premises. Monthly billing starts from day one.' },
            ]?.map(item => (
              <div key={item?.step} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="text-[#ffd700] font-black text-4xl mb-4 opacity-40">{item?.step}</div>
                <h3 className="text-white font-bold text-base mb-2">{item?.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Pricing Tiers */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-black text-white mb-3">Leasing Packages</h2>
            <p className="text-white/40 text-lg">Transparent pricing for every fleet size</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers?.map(tier => (
              <div key={tier?.name} className={`rounded-3xl p-8 border relative ${tier?.featured ? 'scale-[1.03]' : ''}`} style={{ background: tier?.featured ? `${tier?.color}10` : 'rgba(255,255,255,0.04)', borderColor: tier?.featured ? `${tier?.color}40` : 'rgba(255,255,255,0.08)' }}>
                {tier?.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black" style={{ background: tier?.color, color: '#0a1628' }}>Most Popular</div>}
                <div className="mb-6">
                  <h3 className="text-white font-black text-xl mb-1">{tier?.name}</h3>
                  <p className="text-white/40 text-sm">{tier?.vehicles}</p>
                </div>
                <div className="mb-6">
                  <span className="font-black text-3xl" style={{ color: tier?.color }}>{tier?.price}</span>
                  <span className="text-white/40 text-sm ml-1">{tier?.period}</span>
                </div>
                <ul className="space-y-2 mb-8">
                  {tier?.features?.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-white/70">
                      <Icon name="Check" size={14} style={{ color: tier?.color }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/lease-request')} className="w-full py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90" style={{ background: tier?.featured ? tier?.color : 'rgba(255,255,255,0.08)', color: tier?.featured ? '#0a1628' : 'white' }}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.08) 0%, rgba(10,22,40,0) 100%)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-black text-white mb-4">Ready to Lease Your Fleet?</h2>
          <p className="text-white/50 text-lg mb-8">Join 50+ companies that trust OnTheGo for their corporate mobility needs.</p>
          <button onClick={() => navigate('/lease-request')} className="btn-primary text-lg px-10 py-5">
            Request a Quote Today <Icon name="ArrowRight" size={20} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default CorporateLeasingScreen;
